import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { ArrowLeft, Calendar, Check, Download, FileText, PenLine, Sparkles, X } from "lucide-react";
import { getCvByKey, type CvKey, type CvOption } from "../../data/profileData";
import { BOOKING_URL } from "../../lib/brand";
import { CV_SKILL_AREAS, type CvSkillArea } from "./cvFinderData";

const API_URL = import.meta.env.VITE_API_URL || "https://api.geekcow.com";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type LeadStatus = "idle" | "sending" | "sent" | "error";

/**
 * The "Download CV" entry point. Instead of handing over a generic document — or
 * a wall of eleven — it asks the recruiter what SKILLS they're looking for, then
 * one specific follow-up, and returns the single CV that fits. It never leads
 * with Olga's job titles. See cvFinderData.ts for the decision tree and
 * profileData.ts for the CV list.
 *
 * Two side-effects on the result screen:
 *  - every download pings /api/email/cv-download so Olga learns which CV went out;
 *  - the visitor is offered a way to take it further — book a call now, or leave
 *    their details so Olga can reach back.
 */
export function CvFinder({ triggerClassName = "" }: { triggerClassName?: string }) {
  const [open, setOpen] = useState(false);
  const [skillArea, setSkillArea] = useState<CvSkillArea | null>(null);
  const [cvKey, setCvKey] = useState<CvKey | null>(null);

  // Result-screen "take it further" state.
  //  - "details" — the plain "leave your details" contact form
  //  - "skills"  — the "not quite the role? tell me what you need" form, which
  //                asks the visitor to describe the skills/role they're after
  const [contactMode, setContactMode] = useState<null | "details" | "skills">(null);
  const [lead, setLead] = useState({ name: "", email: "", message: "" });
  const [leadStatus, setLeadStatus] = useState<LeadStatus>("idle");
  const [leadError, setLeadError] = useState<string | null>(null);
  /** The CV key we've already emailed a download notification for — stops double-clicks re-sending. */
  const notifiedKey = useRef<CvKey | null>(null);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const resetContact = () => {
    setContactMode(null);
    setLead({ name: "", email: "", message: "" });
    setLeadStatus("idle");
    setLeadError(null);
  };

  const reset = () => {
    setSkillArea(null);
    setCvKey(null);
    resetContact();
  };

  const close = () => {
    setOpen(false);
    // Let the exit animation play before clearing, so it doesn't flash to step 1.
    window.setTimeout(reset, 250);
  };

  // Whenever the chosen CV changes, clear the contact UI so it doesn't carry over.
  useEffect(() => {
    resetContact();
  }, [cvKey]);

  // Escape to close, and lock body scroll while the modal is up.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => event.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const step = cvKey ? 2 : skillArea ? 1 : 0;
  const recommendedCv = cvKey ? getCvByKey(cvKey) : null;

  /** POST a CV event to the mail service. Resolves false if reCAPTCHA isn't ready. */
  const postCvEvent = useCallback(
    async (kind: "download" | "lead", cv: CvOption, extra: Record<string, string> = {}) => {
      if (!executeRecaptcha) return false;
      const recaptchaToken = await executeRecaptcha(kind === "lead" ? "cv_lead" : "cv_download");
      const response = await fetch(`${API_URL}/api/email/cv-download`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ kind, cvRole: cv.role, cvFile: cv.file, ...extra, recaptchaToken }),
      });
      if (!response.ok) throw new Error("Notification failed");
      return true;
    },
    [executeRecaptcha]
  );

  // Fire-and-forget: never block the actual file download on the notification.
  const handleDownload = () => {
    if (!recommendedCv || notifiedKey.current === recommendedCv.key) return;
    notifiedKey.current = recommendedCv.key;
    postCvEvent("download", recommendedCv).catch(() => {
      notifiedKey.current = null; // let a retry through if it failed
    });
  };

  const submitLead = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!recommendedCv) return;
    if (!lead.name.trim()) {
      setLeadError("Please add your name.");
      return;
    }
    if (!EMAIL_RE.test(lead.email.trim())) {
      setLeadError("That email doesn't look right.");
      return;
    }
    if (contactMode === "skills" && lead.message.trim().length < 3) {
      setLeadError("Tell me a little about the skills or role you're after.");
      return;
    }
    setLeadError(null);
    setLeadStatus("sending");
    try {
      await postCvEvent("lead", recommendedCv, {
        name: lead.name.trim(),
        email: lead.email.trim(),
        message: lead.message.trim(),
      });
      setLeadStatus("sent");
    } catch {
      setLeadStatus("error");
      setLeadError("Something went wrong — please try again, or book a call instead.");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          triggerClassName ||
          "inline-flex items-center gap-2 rounded-full border-2 border-[#141414] bg-white px-7 py-4 font-bold text-[13px] tracking-[2px] uppercase text-[#141414] transition-all hover:-translate-y-0.5 hover:bg-[#141414] hover:text-white focus:outline-none focus-visible:ring-4 focus-visible:ring-[#141414]/25"
        }
      >
        <span>Download CV</span>
        <Download size={18} strokeWidth={2.2} aria-hidden="true" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-end justify-center p-0 sm:items-center sm:p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div
              className="absolute inset-0 bg-[#141414]/45 backdrop-blur-[2px]"
              onClick={close}
              aria-hidden="true"
            />

            <motion.div
              role="dialog"
              aria-modal="true"
              aria-labelledby="cv-finder-title"
              initial={{ opacity: 0, y: 24, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 24, scale: 0.98 }}
              transition={{ duration: 0.25, ease: "easeOut" }}
              className="relative z-10 flex max-h-[92vh] w-full max-w-[620px] flex-col overflow-hidden rounded-t-[28px] border-2 border-[#141414] bg-[#f7fbfb] shadow-[0_16px_0_-4px_rgba(0,0,0,0.18)] sm:rounded-[28px]"
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-4 border-b border-[#141414]/10 p-5 md:p-6">
                <div className="flex flex-col gap-2">
                  <span className="inline-flex w-fit items-center gap-2 rounded-full border border-[#141414]/10 bg-white px-3 py-1.5 text-[11px] font-medium uppercase tracking-[2px] text-[#003e5b]">
                    <Sparkles size={13} />
                    I want to understand your needs
                  </span>
                  <h2 id="cv-finder-title" className="font-black text-[22px] leading-tight tracking-tight text-[#141414] md:text-[26px]">
                    {step === 2 ? "Here's my best side for you" : "What skills are you looking for?"}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={close}
                  aria-label="Close"
                  className="shrink-0 rounded-full border border-[#141414]/15 bg-white p-2 text-[#141414] transition-colors hover:bg-[#141414] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#141414]"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-y-auto p-5 md:p-6">
                <AnimatePresence mode="wait">
                  {/* Step 0 — skills the recruiter is looking for */}
                  {step === 0 && (
                    <motion.div
                      key="step-skills"
                      initial={{ opacity: 0, x: 12 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -12 }}
                      transition={{ duration: 0.2 }}
                      className="flex flex-col gap-3"
                    >
                      <p className="text-[14px] leading-[1.5] text-[#666]">
                        Pick the skills that matter most for this role. One more question, then your CV.
                      </p>
                      {CV_SKILL_AREAS.map((item) => (
                        <button
                          key={item.value}
                          type="button"
                          onClick={() => setSkillArea(item)}
                          className="group flex items-center justify-between gap-4 rounded-[18px] border border-[#141414]/15 bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:border-[#141414] hover:shadow-[0_6px_0_-2px_rgba(0,0,0,0.16)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#141414]"
                        >
                          <span className="flex flex-col gap-0.5">
                            <span className="font-bold text-[16px] text-[#141414]">{item.label}</span>
                            <span className="text-[13px] leading-[1.4] text-[#666]">{item.skills}</span>
                          </span>
                          <span className="text-[#666] transition-transform group-hover:translate-x-1" aria-hidden="true">
                            →
                          </span>
                        </button>
                      ))}
                    </motion.div>
                  )}

                  {/* Step 1 — specialisation */}
                  {step === 1 && skillArea && (
                    <motion.div
                      key="step-specialisation"
                      initial={{ opacity: 0, x: 12 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -12 }}
                      transition={{ duration: 0.2 }}
                      className="flex flex-col gap-3"
                    >
                      <p className="text-[14px] leading-[1.5] text-[#666]">
                        <span className="font-semibold text-[#141414]">{skillArea.label}</span> — which of these is the priority?
                      </p>
                      {skillArea.options.map((option) => (
                        <button
                          key={option.cv}
                          type="button"
                          onClick={() => setCvKey(option.cv)}
                          className="group flex items-center justify-between gap-4 rounded-[18px] border border-[#141414]/15 bg-white p-4 text-left transition-all hover:-translate-y-0.5 hover:border-[#141414] hover:shadow-[0_6px_0_-2px_rgba(0,0,0,0.16)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#141414]"
                        >
                          <span className="flex flex-col gap-0.5">
                            <span className="font-bold text-[16px] text-[#141414]">{option.label}</span>
                            <span className="text-[13px] leading-[1.4] text-[#666]">{option.hint}</span>
                          </span>
                          <span className="text-[#666] transition-transform group-hover:translate-x-1" aria-hidden="true">
                            →
                          </span>
                        </button>
                      ))}
                    </motion.div>
                  )}

                  {/* Step 2 — the single result */}
                  {step === 2 && recommendedCv && (
                    <motion.div
                      key="step-result"
                      initial={{ opacity: 0, scale: 0.97 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.97 }}
                      transition={{ duration: 0.22 }}
                      className="flex flex-col gap-5"
                    >
                      <div
                        className="flex flex-col gap-4 rounded-[22px] border-2 border-[#141414] bg-white p-5 md:p-6"
                        style={{ boxShadow: `0 10px 0 -3px ${recommendedCv.accent}` }}
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white"
                            style={{ background: recommendedCv.accent }}
                          >
                            <FileText size={20} />
                          </span>
                          <div className="flex flex-col">
                            <span className="font-black text-[20px] leading-tight text-[#141414]">{recommendedCv.role}</span>
                          </div>
                        </div>
                        <p className="text-[14px] leading-[1.55] text-[#666]">{recommendedCv.forWhom}</p>
                        <a
                          href={recommendedCv.file}
                          download
                          onClick={handleDownload}
                          className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-[#141414] bg-[#141414] px-6 py-3.5 font-bold text-[13px] uppercase tracking-[2px] text-white transition-all hover:-translate-y-0.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#141414]/25"
                        >
                          Download {recommendedCv.role} CV
                          <Download size={17} strokeWidth={2.2} aria-hidden="true" />
                        </a>
                      </div>

                      {/* Take it further — book now, or leave details */}
                      <div className="rounded-[22px] border border-[#141414]/10 bg-white p-5 md:p-6">
                        <AnimatePresence mode="wait">
                          {leadStatus === "sent" ? (
                            <motion.div
                              key="lead-sent"
                              initial={{ opacity: 0, scale: 0.97 }}
                              animate={{ opacity: 1, scale: 1 }}
                              className="flex flex-col items-center gap-2 py-2 text-center"
                            >
                              <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#01e12d] text-white">
                                <Check size={22} strokeWidth={3} />
                              </span>
                              <p className="font-bold text-[16px] text-[#141414]">Thanks — I'll be in touch.</p>
                              <p className="text-[13px] leading-[1.5] text-[#666]">
                                Your details are with me. Expect a reply within a day or two.
                              </p>
                            </motion.div>
                          ) : contactMode ? (
                            <motion.form
                              key="lead-form"
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              onSubmit={submitLead}
                              className="flex flex-col gap-3"
                            >
                              <p className="text-[14px] font-semibold text-[#141414]">
                                {contactMode === "skills"
                                  ? "Tell me the skills you need and I'll point you to the right fit."
                                  : "Leave your details and I'll reach out."}
                              </p>
                              <input
                                type="text"
                                placeholder="Your name"
                                autoComplete="name"
                                value={lead.name}
                                onChange={(e) => setLead((p) => ({ ...p, name: e.target.value }))}
                                className="w-full rounded-full border border-[#141414]/15 bg-[#f7fbfb] px-4 py-3 text-[14px] text-[#141414] outline-none focus:border-[#141414] focus-visible:ring-2 focus-visible:ring-[#141414]/20"
                              />
                              <input
                                type="email"
                                placeholder="you@company.com"
                                autoComplete="email"
                                value={lead.email}
                                onChange={(e) => setLead((p) => ({ ...p, email: e.target.value }))}
                                className="w-full rounded-full border border-[#141414]/15 bg-[#f7fbfb] px-4 py-3 text-[14px] text-[#141414] outline-none focus:border-[#141414] focus-visible:ring-2 focus-visible:ring-[#141414]/20"
                              />
                              <textarea
                                placeholder={
                                  contactMode === "skills"
                                    ? "Which skills or role are you hiring for? A couple of lines is plenty…"
                                    : "A line about the role (optional)"
                                }
                                rows={contactMode === "skills" ? 3 : 2}
                                value={lead.message}
                                onChange={(e) => setLead((p) => ({ ...p, message: e.target.value }))}
                                className="w-full resize-none rounded-[18px] border border-[#141414]/15 bg-[#f7fbfb] px-4 py-3 text-[14px] text-[#141414] outline-none focus:border-[#141414] focus-visible:ring-2 focus-visible:ring-[#141414]/20"
                              />
                              {leadError && <p className="text-[13px] font-semibold text-[#f42536]">{leadError}</p>}
                              <div className="flex items-center gap-3">
                                <button
                                  type="submit"
                                  disabled={leadStatus === "sending"}
                                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border-2 border-[#141414] bg-[#ffc803] px-5 py-3 font-bold text-[13px] uppercase tracking-[1.5px] text-[#141414] transition-all hover:-translate-y-0.5 disabled:opacity-60 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#141414]/25"
                                >
                                  {leadStatus === "sending" ? "Sending…" : "Send my details"}
                                </button>
                                <button
                                  type="button"
                                  onClick={resetContact}
                                  className="rounded-full border border-[#141414]/15 bg-white px-4 py-3 text-[12px] font-semibold uppercase tracking-[1.5px] text-[#141414] transition-colors hover:border-[#141414]"
                                >
                                  Cancel
                                </button>
                              </div>
                            </motion.form>
                          ) : (
                            <motion.div
                              key="lead-choice"
                              initial={{ opacity: 0, y: 8 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="flex flex-col gap-3"
                            >
                              <p className="text-[14px] font-semibold text-[#141414]">Want to take it further?</p>
                              <div className="flex flex-col gap-2.5 sm:flex-row">
                                <a
                                  href={BOOKING_URL}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border-2 border-[#141414] bg-[#141414] px-5 py-3 font-bold text-[13px] uppercase tracking-[1.5px] text-white transition-all hover:-translate-y-0.5 focus:outline-none focus-visible:ring-4 focus-visible:ring-[#141414]/25"
                                >
                                  <Calendar size={16} aria-hidden="true" />
                                  Book a call now
                                </a>
                                <button
                                  type="button"
                                  onClick={() => setContactMode("details")}
                                  className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border-2 border-[#141414] bg-white px-5 py-3 font-bold text-[13px] uppercase tracking-[1.5px] text-[#141414] transition-all hover:-translate-y-0.5 hover:bg-[#f7fbfb] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#141414]/25"
                                >
                                  <PenLine size={16} aria-hidden="true" />
                                  Leave your details
                                </button>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>

                      {leadStatus !== "sent" && (
                        <p className="text-center text-[13px] text-[#666]">
                          Not quite the role?{" "}
                          <button
                            type="button"
                            onClick={() => setContactMode("skills")}
                            className="font-semibold text-[#141414] underline underline-offset-4 hover:text-[#003e5b]"
                          >
                            Tell me what you need
                          </button>
                        </p>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Footer — progress + back */}
              <div className="flex items-center justify-between gap-4 border-t border-[#141414]/10 bg-white/60 px-5 py-4 md:px-6">
                <div className="flex items-center gap-1.5" aria-hidden="true">
                  {[0, 1, 2].map((dot) => (
                    <span
                      key={dot}
                      className={`h-1.5 rounded-full transition-all ${
                        dot === step ? "w-6 bg-[#141414]" : dot < step ? "w-1.5 bg-[#141414]" : "w-1.5 bg-[#141414]/20"
                      }`}
                    />
                  ))}
                </div>
                {step === 1 && (
                  <button
                    type="button"
                    onClick={() => setSkillArea(null)}
                    className="inline-flex items-center gap-2 rounded-full border border-[#141414]/15 bg-white px-4 py-2 text-[12px] font-semibold uppercase tracking-[1.6px] text-[#141414] transition-colors hover:border-[#141414] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#141414]"
                  >
                    <ArrowLeft size={15} />
                    Back
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
